/**
 * Attribution registry for site photography (docs/image-sourcing.md).
 *
 * Add one entry per photo as it lands in packages/web/public/images/ — this
 * single array drives web's /credits page and in-hero captions plus the mobile
 * Credits screen (matched against the DB image URL by pathname, so absolute
 * and relative URLs both resolve).
 *
 * CC attribution needs TASL: Title, Author, Source link, License link — plus a
 * note when the image was modified (cropping counts). Only CC BY / CC BY-SA are
 * safe for Atlas (commercial, and we crop); avoid BY-NC and BY-ND.
 */
export type ImageCredit = {
  /** Pathname under public/, e.g. "/images/heroes/lake-district.jpg". */
  image: string;
  /** What it's used for, e.g. "Lake District region hero". Shown on /credits. */
  usedFor: string;
  /** Original photo title (use "Untitled" if the source has none). */
  title: string;
  author: string;
  /** Author profile page, if the source has one. */
  authorUrl?: string;
  /** Page the photo came from (Flickr page, Wikimedia file page, …). */
  sourceUrl: string;
  /** e.g. "CC BY 2.0" */
  license: string;
  /** e.g. "https://creativecommons.org/licenses/by/2.0/" */
  licenseUrl: string;
  /** e.g. "cropped" — required by CC when the image was altered. */
  modifications?: string;
};

export const IMAGE_CREDITS: ImageCredit[] = [
  // ── Template — copy, fill in, delete this comment block when done ──────────
  // {
  //   image: "/images/heroes/lake-district.jpg",
  //   usedFor: "Lake District region hero",
  //   title: "Derwentwater from Catbells",
  //   author: "Jane Smith",
  //   authorUrl: "https://www.flickr.com/people/janesmith/",
  //   sourceUrl: "https://www.flickr.com/photos/janesmith/12345678/",
  //   license: "CC BY 2.0",
  //   licenseUrl: "https://creativecommons.org/licenses/by/2.0/",
  //   modifications: "cropped",
  // },
  {
    image: "/images/covers/pennine-way.jpg",
    usedFor: "Pennine Way collection cover",
    title: "Pennine Way sign Keld",
    author: "Kreuzschnabel",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Kreuzschnabel",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:2014_Pennine_Way_sign_Keld.jpg",
    license: "CC BY-SA 3.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0",
    modifications: "cropped",
  },
  {
    image: "/images/covers/cleveland-way.jpg",
    usedFor: "Cleveland Way collection cover",
    title: "Cleveland Way signpost",
    author: "photoeverywhere",
    authorUrl: "http://photoeverywhere.co.uk/",
    sourceUrl: "https://www.freeimageslive.co.uk/free_stock_image/cleveland-way-whitby-jpg",
    license: "CC BY 3.0",
    licenseUrl: "https://creativecommons.org/licenses/by/3.0/",
    modifications: "cropped",
  },
  {
    image: "/images/covers/ridgeway.jpg",
    usedFor: "Ridgeway collection cover",
    title: "Ridgeway sign",
    author: "UKgeofan",
    authorUrl: "https://commons.wikimedia.org/wiki/User:UKgeofan",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Ridgeway_National_Trail_signpost.JPG",
    license: "CC BY-SA 3.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0",
    modifications: "cropped",
  },
  // ── Unsplash — attribution not required by the license, credited anyway ────
  {
    image: "/images/covers/munros.jpg",
    usedFor: "Munros collection cover",
    title: "Lochnagar, White Mounth Munro Circuit",
    author: "James Montgomerie",
    authorUrl: "https://unsplash.com/@jammyd555",
    sourceUrl: "https://unsplash.com/photos/green-grass-field-near-gray-rock-formation-during-daytime-YQPgLNUPzLw",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/covers/wainwrights.jpg",
    usedFor: "Wainwrights collection cover",
    title: "Untitled",
    author: "Jonny Gios",
    authorUrl: "https://unsplash.com/@supergios",
    sourceUrl: "https://unsplash.com/photos/a-view-of-a-mountain-range-with-a-grassy-field-in-the-foreground-Ype_waoHbKY",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/covers/three-peaks.jpg",
    usedFor: "Yorkshire Three Peaks collection cover",
    title: "Snowdon at sunset",
    author: "Neil Thomas",
    authorUrl: "https://unsplash.com/@neilthomas",
    sourceUrl: "https://unsplash.com/photos/silhouette-of-mountain-ranges-7f_V1F1MbuI",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/covers/welsh-3000s.jpg",
    usedFor: "Welsh 3000ers collection cover",
    title: "Miners Trail, Snowdonia",
    author: "Josh Kirk",
    authorUrl: "https://unsplash.com/@this_josh",
    sourceUrl: "https://unsplash.com/photos/lake-beside-mountain-under-cloudy-sky-T_3JkNB08tk",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/covers/south-west-coast-path.jpg",
    usedFor: "South West Coast Path trail cover",
    title: "South West Coast Path sign at Mount Batten, Plymouth",
    author: "Patrick Owen PR",
    authorUrl: "https://unsplash.com/@owenpr",
    sourceUrl: "https://unsplash.com/photos/a-wooden-sign-pointing-in-different-directions-Z6dPoX0ZVQM",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/covers/south-downs-way.jpg",
    usedFor: "South Downs Way trail cover",
    title: "South Downs Way wooden signpost",
    author: "Annie Spratt",
    authorUrl: "https://unsplash.com/@anniespratt",
    sourceUrl: "https://unsplash.com/photos/brown-wooden-signage-during-daytime-GPCgLbBevQg",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/covers/cotswold-way.jpg",
    usedFor: "Cotswold Way trail cover",
    title: "Untitled",
    author: "Jason",
    authorUrl: "https://unsplash.com/@levelupfilming",
    sourceUrl: "https://unsplash.com/photos/a-wooden-sign-pointing-in-different-directions-in-front-of-a-tree-yBzNoNl6Hyo",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/covers/trig-points.jpg",
    usedFor: "Trig Points collection cover",
    title: "Untitled",
    author: "Andrew Ridley",
    authorUrl: "https://unsplash.com/@aridley88",
    sourceUrl: "https://unsplash.com/photos/man-standing-in-the-middle-of-the-field-Y8IBde5kHEU",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/heroes/lake-district.jpg",
    usedFor: "Lake District region hero",
    title: "Autumn over Crummock Water",
    author: "Tobias Keller",
    authorUrl: "https://unsplash.com/@tokeller",
    sourceUrl: "https://unsplash.com/photos/mountain-ranges-ihnCmYiHyrU",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/heroes/snowdonia.jpg",
    usedFor: "Snowdonia region hero",
    title: "Lakes of Snowdonia",
    author: "Siddhant Kumar",
    authorUrl: "https://unsplash.com/@saytosid",
    sourceUrl: "https://unsplash.com/photos/lake-near-mountains-hdMigxBJBKk",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/heroes/brecon-beacons.jpg",
    usedFor: "Brecon Beacons region hero",
    title: "Sunrise from Pen y Fan",
    author: "Samuel Thompson",
    authorUrl: "https://unsplash.com/@samuelthomps0n",
    sourceUrl: "https://unsplash.com/photos/aerial-view-photography-of-mountain-range-under-golden-hour-gfkNnpMdu-Q",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/heroes/dartmoor.jpg",
    usedFor: "Dartmoor region hero",
    title: "Bowerman's Nose, Dartmoor",
    author: "Terry Montague",
    authorUrl: "https://unsplash.com/@devon_terry",
    sourceUrl: "https://unsplash.com/photos/brown-rock-formation-on-green-grass-field-during-daytime-1DjIOOweefU",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/heroes/peak-district.jpg",
    usedFor: "Peak District region hero",
    title: "Untitled",
    author: "Tom Wheatley",
    authorUrl: "https://unsplash.com/@mikeballet",
    sourceUrl: "https://unsplash.com/photos/a-view-of-a-rocky-hillside-with-grass-and-rocks-IaqdvZBERIA",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  {
    image: "/images/heroes/yorkshire-dales.jpg",
    usedFor: "Yorkshire Dales region hero",
    title: "Untitled",
    author: "Jamie Davies",
    authorUrl: "https://unsplash.com/@jamie_davies",
    sourceUrl: "https://unsplash.com/photos/brown-and-green-mountains-under-white-sky-during-daytime-NcgqbpUtdVw",
    license: "Unsplash License",
    licenseUrl: "https://unsplash.com/license",
  },
  // ── Wikimedia Commons / Geograph (CC) — batch sourced 2026-07-18 ──────────
  {
    image: "/images/covers/corbetts.jpg",
    usedFor: "Corbetts collection cover",
    title: "Ben Arthur, Arrochar Alps, Scotland 02",
    author: "Michal Klajban",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Podzemnik",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Ben_Arthur,_Arrochar_Alps,_Scotland_02.jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/grahams.jpg",
    usedFor: "Fionas collection cover",
    title: "Ben Venue from Loch Katrine",
    author: "Thrissel",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Thrissel",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Ben_Venue_from_Loch_Katrine.JPG",
    license: "Public domain",
    licenseUrl: "https://creativecommons.org/publicdomain/mark/1.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/donalds.jpg",
    usedFor: "Donalds collection cover",
    title: "View of Tinto from Broad Hill",
    author: "Richard Webb",
    authorUrl: "https://www.geograph.org.uk/profile/196",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:View_of_Tinto_from_Broad_Hill_-_geograph.org.uk_-_7582720.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/donald-tops.jpg",
    usedFor: "Donald Tops collection cover",
    title: "Saddle Yoke rises above Bodesbeck",
    author: "Alan O'Dowd",
    authorUrl: "https://www.geograph.org.uk/profile/34944",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Saddle_Yoke_rises_above_Bodesbeck_-_geograph.org.uk_-_6933555.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/nuttalls.jpg",
    usedFor: "Nuttalls collection cover",
    title: "On Aran Fawddwy",
    author: "Michael Graham",
    authorUrl: "https://www.geograph.org.uk/profile/3141",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:On_Aran_Fawddwy_-_geograph.org.uk_-_2881488.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/hewitts.jpg",
    usedFor: "Hewitts collection cover",
    title: "High Cup Gill",
    author: "Douglal",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Douglal",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:High_Cup_Gill.jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/birketts.jpg",
    usedFor: "Birketts collection cover",
    title: "Path on Loughrigg Fell",
    author: "Bill Boaden",
    authorUrl: "https://www.geograph.org.uk/profile/42541",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Path_on_Loughrigg_Fell_-_geograph.org.uk_-_2127009.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/marilyns.jpg",
    usedFor: "Marilyns collection cover",
    title: "Ailsa Craig Scotland 2022",
    author: "Niels Johannes",
    authorUrl: "https://commons.wikimedia.org/wiki/User:N._Johannes",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Ailsa_Craig_Scotland_2022.jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/ethels.jpg",
    usedFor: "Ethels collection cover",
    title: "Mam Tor Ridge from Lose Hill, Derbyshire",
    author: "Andrew Tryon",
    authorUrl: "https://www.geograph.org.uk/profile/104377",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Mam_Tor_Ridge_from_Lose_Hill,_Derbyshire_-_geograph.org.uk_-_5808252.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/hadrians-wall-path.jpg",
    usedFor: "Hadrian's Wall Path trail cover",
    title: "The Pennine Way & Hadrian's Wall Path near Turret 39B",
    author: "Dave Kelly",
    authorUrl: "https://www.geograph.org.uk/profile/84364",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:The_Pennine_Way_%26_Hadrian%27s_Wall_Path_near_Turret_39B_-_geograph.org.uk_-_7216451.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/north-downs-way.jpg",
    usedFor: "North Downs Way trail cover",
    title: "Walking the pilgrims way North Downs Way near Hollingbourne",
    author: "Ray B's world",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Walking_the_pilgrims_way_North_Downs_Way_near_Hollingbourne.jpg",
    license: "CC BY 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/offas-dyke-path.jpg",
    usedFor: "Offa's Dyke Path trail cover",
    title: "Signpost on Offa's Dyke Path (9652)",
    author: "Nilfanion",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Nilfanion",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Signpost_on_Offa%27s_Dyke_Path_(9652).jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/peddars-way.jpg",
    usedFor: "Peddars Way & Norfolk Coast Path trail cover",
    title: "Signpost on The Peddars' Way",
    author: "Richard Humphrey",
    authorUrl: "https://www.geograph.org.uk/profile/39484",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Signpost_on_The_Peddars%27_Way_-_geograph.org.uk_-_4091692.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/pennine-bridleway.jpg",
    usedFor: "Pennine Bridleway trail cover",
    title: "Signpost on the Pennine Bridleway",
    author: "Bill Boaden",
    authorUrl: "https://www.geograph.org.uk/profile/42541",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Signpost_on_the_Pennine_Bridleway_-_geograph.org.uk_-_4367178.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/thames-path.jpg",
    usedFor: "Thames Path trail cover",
    title: "Signpost for the Thames Path",
    author: "Steve Daniels",
    authorUrl: "https://www.geograph.org.uk/profile/35305",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Signpost_for_the_Thames_Path_-_geograph.org.uk_-_7794620.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/yorkshire-wolds-way.jpg",
    usedFor: "Yorkshire Wolds Way trail cover",
    title: "Yorkshire Wolds Way at Turtle Hill",
    author: "Chris Morgan",
    authorUrl: "https://www.geograph.org.uk/profile/69729",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Yorkshire_Wolds_Way_at_Turtle_Hill_(geograph_4342501).jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/covers/coast-to-coast.jpg",
    usedFor: "Coast to Coast trail cover",
    title: "Coast to coast signpost Rogan's Seat",
    author: "Kreuzschnabel",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Kreuzschnabel",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Coast_to_coast_signpost_Rogan%27s_Seat.jpg",
    license: "CC BY-SA 3.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/cairngorms.jpg",
    usedFor: "Cairngorms region hero",
    title: "Loch Morlich in Scotland",
    author: "Caroline Legg",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Loch_Morlich_in_Scotland.jpg",
    license: "CC BY 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by/2.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/exmoor.jpg",
    usedFor: "Exmoor region hero",
    title: "Castle Rock, Valley of Rocks",
    author: "Nilfanion",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Nilfanion",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Castle_Rock,_Valley_of_Rocks.jpg",
    license: "CC BY-SA 3.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/northumberland.jpg",
    usedFor: "Northumberland region hero",
    title: "Hadrian's Wall west of Housesteads 3",
    author: "Steven Fruitsmaak",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Stevenfruitsmaak",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Hadrian%27s_Wall_west_of_Housesteads_3.jpg",
    license: "Public domain",
    licenseUrl: "https://creativecommons.org/publicdomain/mark/1.0/",
    modifications: "resized",
  },
  {
    image: "/images/heroes/north-york-moors.jpg",
    usedFor: "North York Moors region hero",
    title: "The sea of purple in North York Moors 04",
    author: "Thelight00702",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:The_sea_of_purple_in_North_York_Moors_04.jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/pembrokeshire-coast.jpg",
    usedFor: "Pembrokeshire Coast region hero",
    title: "Green Bridge of Wales 090725 118",
    author: "Sanderling6451",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Green_Bridge_of_Wales_090725_118.jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/new-forest.jpg",
    usedFor: "New Forest region hero",
    title: "Rockford Common",
    author: "Oast House Archive",
    authorUrl: "https://www.geograph.org.uk/profile/10354",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Rockford_Common_-_geograph.org.uk_-_5418930.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/south-downs.jpg",
    usedFor: "South Downs region hero",
    title: "Seven Sisters Panorama, East Sussex, England - May 2009",
    author: "Diliff",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Diliff",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Seven_Sisters_Panorama,_East_Sussex,_England_-_May_2009.jpg",
    license: "CC BY-SA 3.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/the-broads.jpg",
    usedFor: "The Broads region hero",
    title: "Sunset at Thurne Mill",
    author: "Michael Westley",
    authorUrl: "https://www.geograph.org.uk/profile/42460",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Sunset_at_Thurne_Mill_-_geograph.org.uk_-_7230634.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/loch-lomond-and-the-trossachs.jpg",
    usedFor: "Loch Lomond & The Trossachs region hero",
    title: "Loch Lomond from Conic Hill",
    author: "Craigmacl",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Loch_Lomond_from_Conic_Hill.jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/scottish-highlands.jpg",
    usedFor: "Scottish Highlands region hero",
    title: "Lagangarbh cottage with Buachaille Etive Mòr",
    author: "Colin",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Colin",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Lagangarbh_cottage_with_Buachaille_Etive_M%C3%B2r.jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/southern-uplands.jpg",
    usedFor: "Southern Uplands region hero",
    title: "Moffat Hills (50181647951)",
    author: "caroline legg",
    authorUrl: "https://www.flickr.com/people/128941223@N02",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Moffat_Hills_(50181647951).jpg",
    license: "CC BY 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by/2.0",
    modifications: "resized",
  },
  {
    image: "/images/heroes/galloway-hills.jpg",
    usedFor: "Galloway Hills region hero",
    title: "View from Merrick, Dumfries and Galloway",
    author: "MPerkowski",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:View_from_Merrick,_Dumfries_and_Galloway.jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0",
    modifications: "resized",
  },
  // ── Geography round covers (ATL-91, 2026-10-08) ─────────────────────────────
  {
    image: "/images/covers/bothies.jpg",
    usedFor: "Bothies collection cover",
    title: "Warnscale Head Bothy",
    author: "Brian Deegan",
    authorUrl: "https://www.geograph.org.uk/profile/123467",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Warnscale_Head_Bothy,_Lake_District_-_geograph.org.uk_-_7299375.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/great-glen-way.jpg",
    usedFor: "Great Glen Way trail cover",
    title: "Great Glen Way sign",
    author: "Peter S",
    authorUrl: "https://www.geograph.org.uk/profile/19276",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Great_Glen_Way_sign_-_geograph.org.uk_-_7557861.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/john-muir-way.jpg",
    usedFor: "John Muir Way trail cover",
    title: "Signpost on the John Muir Way",
    author: "Raibeart MacAoidh",
    authorUrl: "https://www.geograph.org.uk/profile/24401",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Signpost_on_the_John_Muir_Way_-_geograph.org.uk_-_7549036.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/rob-roy-way.jpg",
    usedFor: "Rob Roy Way trail cover",
    title: "Rob Roy Way sign south of Loch Tay",
    author: "Grinner",
    authorUrl: "https://commons.wikimedia.org/wiki/User:Grinner",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Rob_roy_way_sign.jpg",
    license: "CC BY-SA 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/4.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/southern-upland-way.jpg",
    usedFor: "Southern Upland Way trail cover",
    title: "Signpost on the Southern Upland Way",
    author: "Alan O'Dowd",
    authorUrl: "https://www.geograph.org.uk/profile/34944",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Signpost_on_Southern_Upland_Way_-_geograph.org.uk_-_5800928.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/sperrins.jpg",
    usedFor: "Sperrin Mountains collection cover",
    title: "Sawel Mountain across the glen",
    author: "Kifster",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Sawel_mountain.jpg",
    license: "CC BY-SA 3.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/speyside-way.jpg",
    usedFor: "Speyside Way trail cover",
    title: "Speyside Way signpost near Buckpool",
    author: "Dave Kelly",
    authorUrl: "https://www.geograph.org.uk/profile/84364",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Signpost_on_the_Speyside_Way_near_Buckpool_-_geograph.org.uk_-_5540006.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/st-cuthberts-way.jpg",
    usedFor: "St Cuthbert's Way trail cover",
    title: "Waymark, St Cuthbert's Way",
    author: "Richard Webb",
    authorUrl: "https://www.geograph.org.uk/profile/196",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Waymark,_St_Cuthbert's_Way_-_geograph.org.uk_-_8180900.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/viewpoints.jpg",
    usedFor: "Viewpoints collection cover",
    title: "Orrest Head toposcope and view over Windermere",
    author: "Tim Heaton",
    authorUrl: "https://www.geograph.org.uk/profile/3240",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Orrest_Head_toposcope,_and_view_over_Lake_Windermere_-_geograph.org.uk_-_5116433.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/west-highland-way.jpg",
    usedFor: "West Highland Way trail cover",
    title: "West Highland Way waymark near Drymen",
    author: "Preinzi",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:West_Highland_Waymark_(85308493).jpeg",
    license: "CC BY-SA 3.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/cape-wrath-trail.jpg",
    usedFor: "Cape Wrath Trail cover",
    title: "Cape Wrath Trail 1",
    author: "Sebastian Steude",
    authorUrl: "https://www.sebastian-steude.de/",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Cape_Wrath_Trail_1.jpg",
    license: "CC BY 4.0",
    licenseUrl: "https://creativecommons.org/licenses/by/4.0/",
    modifications: "recompressed",
  },
  {
    image: "/images/covers/cateran-trail.jpg",
    usedFor: "Cateran Trail cover",
    title: "Weathered Cateran Trail fingerpost in woodland",
    author: "Richard Webb",
    sourceUrl: "https://www.geograph.org.uk/photo/5144923",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "unmodified",
  },
  {
    image: "/images/covers/causeway-coast-way.jpg",
    usedFor: "Causeway Coast Way cover",
    title: "Causeway Coast Way sign near Portbraddan",
    author: "Rossographer",
    sourceUrl: "https://www.geograph.org.uk/photo/818720",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "unmodified",
  },
  {
    image: "/images/covers/caves.jpg",
    usedFor: "Caves & Caverns cover",
    title: "Entrance to Smoo Cave, Durness",
    author: "Florian Fuchs",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Entrance_to_Smoo_Cave.jpg",
    license: "CC BY 3.0",
    licenseUrl: "https://creativecommons.org/licenses/by/3.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/cumbria-way.jpg",
    usedFor: "Cumbria Way cover",
    title: "Cumbria Way plaque on a drystone wall",
    author: "Adrian Taylor",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Cumbria_Way_sign_-_geograph.org.uk_-_7908401.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/dales-high-way.jpg",
    usedFor: "Dales High Way cover",
    title: "Three Peaks fingerpost with a Dales High Way roundel",
    author: "Stephen Craven",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Sign_for_the_Dales_High_Way_-_geograph.org.uk_-_8317989.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/dales-way.jpg",
    usedFor: "Dales Way cover",
    title: "Dales Way stone marker, Ilkley 81 miles",
    author: "Mark Percy",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Dales_Way_marker_-_geograph.org.uk_-_4985876.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/east-highland-way.jpg",
    usedFor: "East Highland Way cover",
    title: "East Highland Way waymarker, Insh Farm",
    author: "Graham Robson",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:East_Highland_Way_waymarker,_Insh_Farm_-_geograph.org.uk_-_5830259.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/fife-coastal-path.jpg",
    usedFor: "Fife Coastal Path cover",
    title: "Fife Coastal Path fingerpost by the sea",
    author: "Bill Kasman",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Fife_Coastal_Path_route_sign_-_geograph.org.uk_-_6319411.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/glyndwrs-way.jpg",
    usedFor: "Glyndŵr's Way cover",
    title: "Glyndŵr's Way fingerpost against blue sky",
    author: "Alan Hughes",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Glynd%C5%B5r%E2%80%99s_Way_signpost_-_geograph.org.uk_-_8330065.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized, cropped",
  },
  {
    image: "/images/covers/kintyre-way.jpg",
    usedFor: "Kintyre Way cover",
    title: "Kintyre Way timber fingerpost near Carradale",
    author: "thejackrustles",
    sourceUrl: "https://www.geograph.org.uk/photo/7774644",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized, cropped",
  },
  {
    image: "/images/covers/mountain-huts-and-shelters.jpg",
    usedFor: "Mountain Huts & Shelters cover",
    title: "Summit shelter on Cadair Idris",
    author: "Rude Health",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Summit_Shelter_Cadair_Idris_-_Penygadair_-_geograph.org.uk_-_3356009.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/skye-trail.jpg",
    usedFor: "Skye Trail cover",
    title: "The Trotternish ridge",
    author: "David Purchase",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:The_Trotternish_ridge_-_geograph.org.uk_-_5849315.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized",
  },
  {
    image: "/images/covers/two-moors-way.jpg",
    usedFor: "Two Moors Way cover",
    title: "Two Moors Way stone and fingerpost, Drewsteignton",
    author: "A J Paxton",
    sourceUrl: "https://commons.wikimedia.org/wiki/File:Two_Moors_Way,_Drewsteignton_-_geograph.org.uk_-_8220491.jpg",
    license: "CC BY-SA 2.0",
    licenseUrl: "https://creativecommons.org/licenses/by-sa/2.0/",
    modifications: "resized, cropped",
  },
];

/**
 * Look up the credit for an image URL as stored in the DB (absolute or
 * relative). Matches on pathname so "https://goatlas.co.uk/images/x.jpg" and
 * "/images/x.jpg" both resolve to the same entry.
 */
export function findImageCredit(src: string | null | undefined): ImageCredit | undefined {
  if (!src) return undefined;
  let path = src;
  if (/^https?:\/\//i.test(src)) {
    try {
      path = new URL(src).pathname;
    } catch {
      return undefined;
    }
  }
  return IMAGE_CREDITS.find((c) => c.image === path);
}
